/* eslint-disable jsx-a11y/aria-role */
import { h, Fragment } from 'preact'
import { createUniqueId } from '@nrk/dh-utils'
import { FormattedNumber } from '../../../../../lib/intl/FormattedNumber'
import { OppslutningTableItem } from '../mapping'
import { useMemo } from 'preact/hooks'
import { Message } from '../../../../../lib/intl/Message'
import { Link } from '../../../shared/Link'
import { useLocalizedValue } from '../../../../../lib/intl/IntlProvider'
import { Partinavn } from '../../../shared/Partinavn/Partinavn'
import {
  TableHeaderCell,
  TableCell,
  TableFooter,
  TableFooterToggleButton,
  Table
} from '../shared/Table'
import { Icon } from '../../../shared/Icons/Icons'
import { NummerMedEnhet } from '../../../shared/NummerMedEnhet/NummerMedEnhet'
import { flatSingle } from '../../../../../lib/array'

/* styles */
import tableStyles from '../shared/Table.css'

export function calculateBarWidthPercent(percent: number, maxPercent: number = 0): number {
  if (isNaN(percent) || maxPercent <= 0) {
    return 0
  }
  return (percent * 100) / maxPercent
}

function limitAndSortItems(items, maxItems) {
  return [...items]
    .sort((a, b) => {
      if (a.prioritized !== b.prioritized) {
        return a.prioritized ? -1 : 1
      } else {
        return b.prosent - a.prosent
      }
    })
    .slice(0, maxItems)
}

type Props = {
  items: OppslutningTableItem[]
  type: 'steder' | 'partier'
  minimumShowing: number
  maxItems: number
}

export function OppslutningTable(props: Props) {
  const selector = useMemo(createUniqueId, [])
  const { items, minimumShowing, maxItems, type } = props
  const localize = useLocalizedValue()

  const limitedItems = limitAndSortItems(items, maxItems)

  const hasAntall = limitedItems.every(item => item.antall !== undefined)
  const hasPrioritized = limitedItems.some(item => item.prioritized)

  const maksProsent = limitedItems.reduce((acc, item) => Math.max(acc, item.prosent || 0), 45)
  const hasMore = limitedItems.length > minimumShowing

  const isLimited = items.length > maxItems
  const expandStart = Math.min(limitedItems.length, minimumShowing)
  const limitStart = limitedItems.length

  return (
    <Fragment>
      <Table
        data-expand-selector={selector}
        data-expand-start={expandStart}
        modifiers={{ hasMore, hasAntall, isLimited }}
        style={{ '--expand-start': expandStart, '--expand-border-start': limitStart }}
      >
        <TableHeaderCell index={0} modifier="parti">
          {type === 'partier' ? (
            <Message id="oppslutning-parti" />
          ) : (
            <Message id="oppslutning-sted" />
          )}
        </TableHeaderCell>
        <TableHeaderCell index={0} modifier="bar" />
        <TableHeaderCell index={0} modifier="oppslutning">
          <Message id="oppslutning-prosent" />
        </TableHeaderCell>
        {hasAntall ? (
          <TableHeaderCell index={0} modifier="antall">
            <Message id="oppslutning-antall" />
          </TableHeaderCell>
        ) : null}

        {flatSingle(
          limitedItems.map((item, idx) => {
            const index = idx + 1
            const style = { '--item-index': index }
            const width = calculateBarWidthPercent(item.prosent, maksProsent)
            const isHidden = index > minimumShowing
            const farge = hasPrioritized && !item.prioritized ? item.fargeSvak : item.farge

            return [
              <TableCell
                key={`p-${idx}`}
                index={index}
                modifiers={['parti']}
                isHidden={isHidden}
                style={style}
              >
                <Link href={item.url} linkClickType="TABLE">
                  <h4>
                    {item.kortNavn ? (
                      <Partinavn kortNavn={item.kortNavn} navn={item.navn} />
                    ) : (
                      localize(item.navn)
                    )}
                  </h4>
                </Link>
              </TableCell>,
              <TableCell
                key={`b-${idx}`}
                index={index}
                modifiers={['bar']}
                isHidden={isHidden}
                style={style}
                aria-hidden="true"
              >
                <div
                  class={tableStyles.table__bar}
                  style={{ width: width + '%', background: farge }}
                />
              </TableCell>,
              <TableCell
                key={`o-${idx}`}
                index={index}
                modifiers={['oppslutning']}
                isHidden={isHidden}
                style={style}
                role="text"
              >
                <FormattedNumber
                  value={item.prosent}
                  options={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
                />
                {typeof item.prosent === 'number' && (
                  <span class={tableStyles.table__prosent}>&nbsp;%</span>
                )}
              </TableCell>,
              hasAntall ? (
                <TableCell
                  key={`a-${idx}`}
                  index={index}
                  modifiers={['antall']}
                  isHidden={isHidden}
                  style={style}
                  role="text"
                >
                  <NummerMedEnhet value={item.antall} enhetId="enhet-stemmer" />
                </TableCell>
              ) : null
            ]
          })
        )}
      </Table>
      <TableFooter smallScreensOnly={!hasMore}>
        {hasMore && (
          <TableFooterToggleButton
            selector={`[data-expand-selector="${selector}"]`}
            classToToggle={tableStyles.tableShowMore}
            classWhenToggling={tableStyles.tableAnimating}
          >
            <span>
              <Message
                id="oppslutning-vis-alle"
                modifiers={{ partier: type === 'partier', steder: type === 'steder' }}
              />
              <Icon type="nrk-chevron-down" />
            </span>
            <span hidden>
              <Message
                id="oppslutning-vis-færre"
                modifiers={{ partier: type === 'partier', steder: type === 'steder' }}
              />
              <Icon type="nrk-chevron-up" />
            </span>
          </TableFooterToggleButton>
        )}
        {hasAntall ? (
          <TableFooterToggleButton
            smallScreensOnly
            selector={`[data-expand-selector="${selector}"]`}
            classToToggle={tableStyles.tableShowAntall}
          >
            <span>
              <Message id="oppslutning-vis-antall" />
            </span>
            <span hidden>
              <Message id="oppslutning-vis-prosent" />
            </span>
          </TableFooterToggleButton>
        ) : null}
      </TableFooter>
    </Fragment>
  )
}
